import { useState, useEffect, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import Helmet from "react-helmet";
import { Button, Form } from "react-bootstrap";
import Select from "react-select";
import moment from "moment";
import { debounce, isEmpty } from "lodash";
import * as actions from "store/actions";
import * as Path from "routes/paths";
import { PAGE_LIMIT, AUDIT_LOGS } from "common/viewContent";
import { DATE_FORMAT, formatText, userFullName } from "helpers/common";
import { getAuditLogsList, getEventTypesList, getUsersList } from "store/services/auditLogsService";
import SortableTable from "components/shared/SortableTable";
import AppAsyncSelect from "components/shared/FormComponents/AppAsyncSelect";
import ViewDiffPopup from "./Components/ViewDiffPopup";
import ViewMetadataPopup from "./Components/ViewMetadataPopup";
import AdvancedFilterPopup from "./Components/AdvancedFilterPopup";
import ReportDateRange from "../dashboard/Component/ReportDateRange";
import LoadingSkeleton from "components/shared/LoadingSkeleton/Index";

const defaultFilters = {
  order: "desc",
  orderBy: "created_at",
  eventType: "",
  panel: "",
  search: "",
};

const List = ({ dispatch, isLoading }) => {
  const [logsList, setLogsList] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [searchFilter, setSearchFilter] = useState(defaultFilters);
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [eventTypes, setEventTypes] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isFetching, setFetching] = useState(false);

  const fetchAuditLogs = async (cursor = null, reset = false) => {
    try {
      setFetching(true);
      const query = {
        lastCreatedAt: cursor,
        limit: 50,
        orderBy: searchFilter.orderBy,
        order: searchFilter.order,
        eventType: searchFilter.eventType,
        panel: searchFilter.panel,
        user_id: searchFilter.user_id,
        startDate: searchFilter.startDate,
        endDate: searchFilter.endDate,
      };

      dispatch(actions.persist_store({ loader: true }));
      const response = await getAuditLogsList(query, advancedFilters);
      dispatch(actions.persist_store({ loader: false }));

      if (response?.success && response?.body?.data) {
        const newData = reset ? response.body.data : [...logsList, ...response.body.data];
        setLogsList(newData);
        setHasMore(response.body.has_more);
        setNextCursor(response.body.next_cursor);
      } else {
        setLogsList(reset ? [] : logsList);
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error fetching audit logs:", err);
      dispatch(actions.persist_store({ loader: false }));
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchAuditLogs(null, true); // reset data when filters or sorting change
  }, [searchFilter, advancedFilters]);

  useEffect(() => {
    (async () => {
      try {
        const response = await getEventTypesList();
        if (response?.body?.length) {
          setEventTypes(
            response.body.map((eventType) => ({
              value: eventType,
              label: formatText(eventType),
            }))
          );
        }
      } catch (err) {
        console.error("Error fetching event types:", err);
      }
    })();
  }, []);

  const handleLoadMore = () => {
    if (hasMore && nextCursor) fetchAuditLogs(nextCursor, false);
  };

  const handleSearchFilter = (e) => {
    const { name, value } = e.target;
    setSearchFilter((prev) => ({ ...prev, [name]: value }));
    if (name === "dateRangeType" && value === "all") {
      setSearchFilter((prev) => ({ ...prev, startDate: null, endDate: null }));
    }
  };

  const handleSort = (sortBy, sortOrder) => {
    setSearchFilter((prev) => ({ ...prev, orderBy: sortBy, order: sortOrder }));
  };

  const handleDebounceSearch = useCallback(
    debounce((e) => {
      setSearchFilter((prev) => ({ ...prev, search: e.target.value }));
    }, 500),
    []
  );

  const resetFilters = () => {
    setSearchFilter(defaultFilters);
    setSearchValue("");
    fetchAuditLogs(null, true);
  };

  const handleSearchUsers = async (search) => {
    const response = await getUsersList({ search, limit: 100 });
    const users = response?.body?.data || [];
    return users.map((user) => ({
      value: user.id,
      label: `${userFullName(user)} (${user.email})`,
    }));
  };

  const columns = useMemo(
    () => [
      { key: "Log id", render: (item) => item.uuid },
      {
        key: "event_type",
        render: (item) => formatText(item.event_type),
      },
      {
        key: "panel",
        render: (item) => formatText(item.panel),
      },
      {
        key: "User Detail",
        render: (item) => {
          const metadata = item.metadata ? JSON.parse(item.metadata) : {};
          const userInfo = metadata?.userInfo;
          if (!userInfo) return "N/A";
          return (
            <div>
              <p className="m-0"><strong>ID:</strong> {userInfo.id}</p>
              <p className="m-0"><strong>Name:</strong> {userFullName(userInfo)}</p>
              <p className="m-0"><strong>Email:</strong> {userInfo.email}</p>
            </div>
          );
        },
      },
      {
        key: "event_source",
        render: (item) => formatText(item.event_source),
      },
      {
        key: "action_at",
        label: "Event Date & Time",
        sortable: true,
        render: (item) => DATE_FORMAT(item.action_at, "MMM D, YYYY H:mm A"),
      },
      {
        key: "Changes",
        render: (item) => {
          const metadata = item.metadata ? JSON.parse(item.metadata) : {};
          return metadata?.diff ? <ViewDiffPopup diffData={metadata} /> : "N/A";
        },
      },
      {
        key: "Log Metadata",
        render: (item) => {
          const metadata = item.metadata ? JSON.parse(item.metadata) : {};
          return isEmpty(metadata) ? "N/A" : <ViewMetadataPopup diffData={metadata} />;
        },
      },
    ],
    []
  );

  return (
    <>
      <Helmet title={AUDIT_LOGS.CURRENT_MODULE} />
      <section className="section">
        <div className="section-header">
          <h1>{AUDIT_LOGS.CURRENT_MODULE}</h1>
          <div className="section-header-breadcrumb">
            <div className="breadcrumb-item">View Audit Logs</div>
          </div>
        </div>
        <div className="section-body">
          <h2 className="section-title">{AUDIT_LOGS.CURRENT_MODULE}</h2>
          <p className="section-lead">
            In this section, you can view and track all system activities and user actions for auditing purposes. Use the filters to narrow down specific events.
          </p>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h4>{AUDIT_LOGS.TOP_CONTENT}</h4>
            </div>
            <div className="card-body">
            <div className="TableFilter d-flex align-items-center justify-content-between gap-10 flex-wrap">
                    <div className="left d-flex align-items-end gap-10">
                      <div className="">
                        <label
                          htmlFor=""
                          className="form-label fw-sbold m-0 me-2 ws-nowrap"
                        >
                          Search:
                        </label>
                        <div className="searchForm position-relative iconWithText">
                          <AppAsyncSelect
                            onChange={(selected) => handleSearchFilter({ target: {
                              name: "user_id",
                              value: selected?.value
                            }})}
                            fetchOptions={handleSearchUsers}
                            placeholder="Search by User Name/Email"
                            classNamePrefix="nta"
                          />
                        </div>
                      </div>
                      <div className="">
                        <label
                          htmlFor=""
                          className="form-label fw-sbold m-0 me-2 ws-nowrap"
                        >
                          Filter By Panel:
                        </label>
                        <Form.Select
                          aria-label="Default select example"
                          name="panel"
                          className="form-control"
                          onChange={handleSearchFilter}
                          value={searchFilter.panel}
                        >
                          <option value="">All</option>
                          <option value="practitioner">Practitioner</option>
                          <option value="client">Client</option>
                          <option value="admin">Admin</option>
                          <option value="chat">Chat</option>
                        </Form.Select>
                      </div>
                      <div className="">
                        <label
                          htmlFor=""
                          className="form-label fw-sbold m-0 me-2 ws-nowrap"
                        >
                          Filter By Event Type:
                        </label>
                        {/* <Form.Select
                          aria-label="Default select example"
                          name="eventType"
                          className="form-control"
                          onChange={handleSearchFilter}
                        >
                          <option value="all-panels">All</option>
                          <option value="view_client">View Client</option>
                          <option value="view_client_settings">View Client Settings</option>
                          <option value="admin">Admin</option>
                          <option value="chat">Chat</option>
                        </Form.Select> */}
                        <Select
                          value={eventTypes.find(event => event.value === searchFilter.eventType) || null}
                          onChange={(selected) => handleSearchFilter({ target: { name: "eventType", value: selected?.value }})}
                          options={eventTypes}
                          placeholder="Select event type"
                          classNamePrefix="nta"
                        />
                      </div>
                      <div className="">
                        <label
                          htmlFor=""
                          className="form-label fw-sbold m-0 me-2 ws-nowrap"
                        >
                          Filter By Event Date Range:
                        </label>
                        <Form.Select
                          aria-label="Default select example"
                          name="dateRangeType"
                          className="form-control"
                          value={searchFilter?.dateRangeType}
                          onChange={handleSearchFilter}
                        >
                          <option value="all">All Time</option>
                          <option value="custom">Custom</option>
                        </Form.Select>
                      </div>
                      {searchFilter?.dateRangeType === "custom" && (
                        <div>
                          <ReportDateRange
                            start={searchFilter?.startDate}
                            end={searchFilter?.endDate}
                            buttonClassName={"p-2 border"}
                            handleSelectDateRange={(start, end) => {
                              setSearchFilter((prev) => ({
                                ...prev,
                                startDate: moment(start).toISOString(),
                                endDate: moment(end).toISOString(),
                              }))
                            }}
                          // handleChangeType={handleChangeType}
                          />
                        </div>
                      )}
                    </div>
                    <div className="right d-flex align-items-center gap-10">
                      <AdvancedFilterPopup
                          onApply={(filters) => setAdvancedFilters(filters)}
                        />
                        <Button variant="secondary" onClick={resetFilters} className="p-2">Reset Filters</Button>
                      </div>
                  </div>

              <div className="table-responsive">
                <SortableTable
                  columns={columns}
                  data={logsList}
                  // isLoading={isLoading}
                  onSort={handleSort}
                  sortColumn={searchFilter.orderBy}
                  sortOrder={searchFilter.order}
                />
              </div>

              {hasMore && (
                <div className="text-center mt-3">
                  <Button onClick={handleLoadMore} disabled={isLoading}>
                    {isLoading ? "Loading..." : "Load More"}
                  </Button>
                </div>
              )}
            </div>
          </div>
          </div>
            </div>
        </div>
      </section>
    </>
  );
};

const mapStateToProps = (state) => ({
  isLoading: state.persistStore.loader,
});

export default connect(mapStateToProps)(List);
